import { Model, modelRegistry } from './Model';
import { HashGen } from '../utilities';
import { Store } from '../wrappers';
import { apiService, connectionService } from '../services';
import { diContainer } from '../DIContainer';
export class PaymentSession extends Model {
    static entity = 'payment_sessions';
    static primaryKey = 'hash';
    static syncable = true;
    /** transaction_type values the device writes: the card charge and its reversal. */
    static SALE = 'Sale';
    static VOID = 'Void';
    id = null;
    hash = '';
    order_hash = '';
    provider = '';
    amount = '';
    transaction_type = '';
    transaction_class = 'POS';
    status = 'pending';
    request_data = null;
    response_data = null;
    date = null;
    finalized_at = null;
    created_at = null;
    updated_at = null;
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.long()),
            order_hash: this.attr(''),
            provider: this.attr(''),
            amount: this.attr(''),
            transaction_type: this.attr(''),
            transaction_class: this.attr('POS'),
            status: this.attr('pending'),
            request_data: this.attr(null),
            response_data: this.attr(null),
            date: this.attr(null),
            finalized_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    /**
     * A snapshot from the server or a peer must never regress a row this device
     * already finalized: the drain writes the server's copy of the queued CREATE
     * (still pending) back after the terminal has answered, and until the UPDATE
     * drains the answer exists only here. Keeps the server identity, keeps the
     * local outcome — the rule PaymentSessionSyncService applies server-side.
     */
    static withLocalOutcome(incoming) {
        const local = incoming.hash ? PaymentSession.find(incoming.hash) : undefined;
        if (!local?.finalized_at || incoming.finalized_at)
            return incoming;
        return { ...incoming, status: local.status, response_data: local.response_data, finalized_at: local.finalized_at };
    }
    static insert(sendToStore) {
        super.insert({ data: sendToStore.data.map(record => PaymentSession.withLocalOutcome(record)) });
    }
    /**
     * Save the row and queue it for the drain — durable before anything else
     * happens. Returns the queue item so push() can back it up if it cannot be
     * delivered.
     */
    async queue(action) {
        const syncData = await this.markForSync(action);
        this.$save();
        return syncData;
    }
    /**
     * Tell peer terminals about this row as it stands. Called at launch too, so a
     * second terminal's pending-refund and ambiguity guards see a charge or
     * reversal the moment it starts — no server round trip, so nothing can race
     * the terminal's answer.
     */
    announce() {
        PaymentSession.syncTerminals({ items: [{ action: 'post', record: this.$toJson() }] });
    }
    /**
     * Broadcast the finalized row to peer terminals and, when online, upsert it
     * on the server, which removes every queued action for the hash. Offline or
     * on a transport error the row stays queued for the drain and its queue item
     * is copied to the backup store (as Order does): a drain deletes every queue
     * item present when it finishes, including ones added while it ran, and the
     * backup copy is what a manual replay can still deliver.
     */
    async push(queued) {
        let record = this.$toJson();
        if (!Store.get('isOnline')) {
            await queued?.saveForSyncBackup();
            this.announce();
            return { success: true };
        }
        try {
            const response = await apiService.post('/integrations/payments/sessions', record);
            if (response.data.record) {
                record = response.data.record;
                this.$fill(record);
                PaymentSession.insert({ data: [record] });
            }
            this.announce();
            await this.removeFromSync();
            return { success: true };
        }
        catch (error) {
            await queued?.saveForSyncBackup();
            // Never silent: the callers run push() in the background and drop its
            // result, so this report is the only sign the server copy is behind. The
            // terminal's receipt JSON stays out of the report — card data has no
            // place in an error tracker.
            const { response_data, ...session } = record;
            diContainer.resolve('errorReporter').reportError(error, {
                paymentSession: { ...session, refund_reference: response_data?.refund_reference ?? null },
            });
            const isOnline = await connectionService.connectionCheckRetry(5000);
            return { success: isOnline };
        }
    }
}
export default PaymentSession;
modelRegistry.register('PaymentSession', PaymentSession);
//# sourceMappingURL=PaymentSession.js.map